import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Check, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractSignPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API_URL}/api/contracts/sign/${token}`)
      .then(res => setContract(res.data))
      .catch(() => toast.error('Contract not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSign = async () => {
    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/contracts/sign/${token}`);
      toast.success('Contract signed successfully!');
      setContract(prev => ({ ...prev, status: 'signed' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <Layout><div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div></Layout>;

  if (!contract) return (
    <Layout><div className="min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="text-xl font-bold">Contract Not Found</h1><p className="text-[#475569]">This contract link may be invalid or expired.</p></div></div></Layout>
  );

  return (
    <Layout>
      <div className="bg-[#FAFAFA] min-h-screen" data-testid="contract-sign-page">
        <section className="container-custom py-10">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-8 border border-[#E2E8F0] rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <FileText size={24} className="text-[#1D4ED8]" />
                <div>
                  <h1 className="text-xl font-bold text-[#0A0F1C]" data-testid="contract-title">{contract.service_type?.replace(/_/g, ' ')} Agreement</h1>
                  <p className="text-sm text-[#475569]">For: {contract.client_name}</p>
                </div>
              </div>

              <div className="bg-[#F8FAFC] p-6 rounded-lg border border-[#E2E8F0] mb-6 max-h-96 overflow-y-auto">
                <pre className="text-sm text-[#475569] whitespace-pre-wrap font-sans leading-relaxed" data-testid="contract-content">{contract.content}</pre>
              </div>

              {contract.status === 'signed' ? (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center" data-testid="contract-signed">
                  <Check size={24} className="text-green-600 mx-auto mb-2" />
                  <p className="font-bold text-green-700">Contract Signed</p>
                  <p className="text-sm text-green-600">Thank you for signing this agreement.</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-[#475569] mb-4">By clicking "Sign Contract", you agree to all terms outlined above.</p>
                  <Button onClick={handleSign} disabled={signing} className="btn-primary-blue w-full" data-testid="sign-contract-btn">
                    {signing ? 'Signing...' : 'Sign Contract'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
